import { useEffect, useRef, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import { Button, Input, Textarea } from "@carry/ui";

interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

interface PromptOptions {
  title: string;
  description?: ReactNode;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  multiline?: boolean;
  required?: boolean;
}

type Resolver = (ok: boolean) => void;
type PromptResolver = (value: string | null) => void;

let openDialog: ((opts: ConfirmOptions, resolve: Resolver) => void) | null = null;
let openPromptDialog: ((opts: PromptOptions, resolve: PromptResolver) => void) | null = null;

export function confirm(opts: ConfirmOptions): Promise<boolean> {
  if (!openDialog) return Promise.resolve(window.confirm(opts.title));
  return new Promise<boolean>((resolve) => openDialog!(opts, resolve));
}

export function prompt(opts: PromptOptions): Promise<string | null> {
  if (!openPromptDialog) {
    return Promise.resolve(window.prompt(opts.title, opts.defaultValue ?? ""));
  }
  return new Promise<string | null>((resolve) => openPromptDialog!(opts, resolve));
}

export function ConfirmDialogHost() {
  const [state, setState] = useState<{ opts: ConfirmOptions; resolve: Resolver } | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    openDialog = (opts, resolve) => setState({ opts, resolve });
    return () => {
      openDialog = null;
    };
  }, []);

  useEffect(() => {
    if (!state) return;
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        state.resolve(false);
        setState(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state]);

  if (!state) return <PromptDialogHost />;
  const { opts, resolve } = state;
  const close = (ok: boolean) => {
    resolve(ok);
    setState(null);
  };
  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        style={{
          position: "fixed",
          inset: 0,
          background: "oklch(0 0 0 / 0.32)",
          display: "grid",
          placeItems: "center",
          zIndex: 100,
          padding: 24,
        }}
        onClick={() => close(false)}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "min(520px, 100%)",
            background: "var(--bg-panel)",
            border: "0.5px solid var(--rule)",
            borderRadius: 4,
            boxShadow: "0 18px 40px oklch(0 0 0 / 0.18)",
          }}
        >
          <div style={{ padding: "16px 20px", borderBottom: "0.5px solid var(--rule-soft)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div id="confirm-dialog-title" style={{ fontWeight: 600, fontSize: 14 }}>
                {opts.title}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label="閉じる"
                onClick={() => close(false)}
              >
                <X size={16} />
              </Button>
            </div>
            {opts.description && (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  color: "var(--ink-3)",
                  whiteSpace: "pre-wrap",
                }}
              >
                {opts.description}
              </div>
            )}
          </div>
          <div
            style={{
              padding: "12px 20px",
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
            }}
          >
            <Button
              ref={cancelRef}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => close(false)}
            >
              {opts.cancelLabel ?? "キャンセル"}
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={() => close(true)}
              style={opts.destructive ? { background: "var(--danger)", color: "#fff" } : undefined}
            >
              {opts.confirmLabel ?? "OK"}
            </Button>
          </div>
        </div>
      </div>
      <PromptDialogHost />
    </>
  );
}

function PromptDialogHost() {
  const [state, setState] = useState<{ opts: PromptOptions; resolve: PromptResolver } | null>(null);
  const [value, setValue] = useState("");
  const cancelRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    openPromptDialog = (opts, resolve) => {
      setValue(opts.defaultValue ?? "");
      setState({ opts, resolve });
    };
    return () => {
      openPromptDialog = null;
    };
  }, []);

  useEffect(() => {
    if (!state) return;
    inputRef.current?.focus();
    inputRef.current?.select?.();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        state.resolve(null);
        setState(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state]);

  if (!state) return null;
  const { opts, resolve } = state;
  const close = (result: string | null) => {
    resolve(result);
    setState(null);
  };
  const canSubmit = !opts.required || value.trim().length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="prompt-dialog-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "oklch(0 0 0 / 0.32)",
        display: "grid",
        placeItems: "center",
        zIndex: 100,
        padding: 24,
      }}
      onClick={() => close(null)}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) close(value);
        }}
        style={{
          width: "min(520px, 100%)",
          background: "var(--bg-panel)",
          border: "0.5px solid var(--rule)",
          borderRadius: 4,
          boxShadow: "0 18px 40px oklch(0 0 0 / 0.18)",
        }}
      >
        <div style={{ padding: "16px 20px", borderBottom: "0.5px solid var(--rule-soft)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div id="prompt-dialog-title" style={{ fontWeight: 600, fontSize: 14 }}>
              {opts.title}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="閉じる"
              onClick={() => close(null)}
            >
              <X size={16} />
            </Button>
          </div>
          {opts.description && (
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: "var(--ink-3)",
                whiteSpace: "pre-wrap",
              }}
            >
              {opts.description}
            </div>
          )}
        </div>
        <div style={{ padding: "12px 20px" }}>
          {opts.multiline ? (
            <Textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={opts.placeholder}
              rows={4}
            />
          ) : (
            <Input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={opts.placeholder}
            />
          )}
        </div>
        <div
          style={{
            padding: "12px 20px",
            borderTop: "0.5px solid var(--rule-soft)",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <Button
            ref={cancelRef}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => close(null)}
          >
            {opts.cancelLabel ?? "キャンセル"}
          </Button>
          <Button type="submit" variant="default" size="sm" disabled={!canSubmit}>
            {opts.confirmLabel ?? "OK"}
          </Button>
        </div>
      </form>
    </div>
  );
}
